import { ApiPrewarming, ApiProductSize, ApiProductStockDetail } from 'idt-ecommerce-api/_api/api-catalog/api-products/api-products.models';
import { EcommerceContext } from 'idt-ecommerce-api/context/context.models';
import { CurrencyInfo } from 'idt-ecommerce-api/stores/stores.models';
import { MoneyHelpers } from 'idt-ecommerce-api/util/money.helpers';

import { ProductPricePrewarming, ProductSize } from '../products.models';


export class SizesModelsTransformer {

  /**
   * Function that cleans the list of sizes and maintains those that have stock in the event that a repeated one does not have it
   * @param apiProductSize list of sizes with repeat sizes
   * @param productStock lis of sizes with stock
   */

  public static filterRepeatSizes(apiProductSize: ApiProductSize[], productStock: ApiProductStockDetail[] = []): ApiProductSize[] {
    const filteredSizes = apiProductSize.reduce((
      result: {[key: string]: ApiProductSize},
      productSize: ApiProductSize
    ): {[key: string]: ApiProductSize} => {
      const hasProductSizeWithStock = productStock.some((sizeWidthStock) => {
        return sizeWidthStock.availability === 'in_stock' && sizeWidthStock.id === productSize.sku;
      });
      if (hasProductSizeWithStock || !result[productSize.name]) {
        result[productSize.name] = productSize;
      }
      return result;
    }, {});

    return Object.values(filteredSizes);
  }

  public static createProductSize(apiSize: ApiProductSize, currencyInfo: CurrencyInfo, productStock: ApiProductStockDetail[] = []): ProductSize {
    const sizeStock = productStock.find((stock: ApiProductStockDetail) => stock.id === apiSize.sku);
    const price: number = parseInt((apiSize?.price ?? ''), 10);
    const oldPrice: number = parseInt((apiSize?.oldPrice ?? ''), 10);
    const oldPriceDiscount = apiSize?.discountsPercentages?.oldPriceDiscount;
    const originalPrice: number = parseInt((apiSize?.originalPrice ?? ''), 10);
    const originalPriceDiscount = apiSize?.discountsPercentages?.originalPriceDiscount;
    const discountsPercentages = { ...(oldPriceDiscount && { oldPriceDiscount }), ...(originalPriceDiscount && { originalPriceDiscount }) };
    return {
      sku: apiSize.sku.toString(),
      masterSizeId: apiSize.mastersSizeId,
      name: apiSize.name,
      partnumber: apiSize.partnumber,
      isBuyable: apiSize.isBuyable,
      backSoon: apiSize.backSoon,
      description: apiSize.description,
      priceInfo: {
        ...(!isNaN(price) && { price }),
        ...(!isNaN(oldPrice) && { oldPrice }),
        ...(!isNaN(originalPrice) && { originalPrice }),
        priceWithCurrencySymbol: price ? MoneyHelpers.formatPriceWithCurrencySymbol(price, currencyInfo) : '',
        oldPriceWithCurrencySymbol: oldPrice ? MoneyHelpers.formatPriceWithCurrencySymbol(oldPrice, currencyInfo) : '',
        discount: MoneyHelpers.getDiscountFromPrice(price, oldPrice),
        prewarming: this.prewarmingFromApi(apiSize.futurePrice, currencyInfo),
        ...((oldPriceDiscount || originalPriceDiscount) && { discountsPercentages })
      },
      availability: (sizeStock
        && (sizeStock.availability === 'in_stock' || sizeStock.availability === 'out_of_stock')) ? sizeStock.availability : 'unknown',
      visibilityValue: apiSize.visibilityValue,
      typeThreshold: sizeStock?.typeThreshold
    };
  }

  public static productSizeObjectFromApi(
    apiProductSize: ApiProductSize[],
    productStock: ApiProductStockDetail[],
    context: EcommerceContext
  ): ProductSize[] {
    const productSizes: ProductSize[] = [];
    const currencyInfo: CurrencyInfo | undefined = context.store.details.currencyInfo;

    if (currencyInfo) {
      const apiProductSizeFiltered: ApiProductSize[] = SizesModelsTransformer.filterRepeatSizes(apiProductSize, productStock);

      for (const apiSize of apiProductSizeFiltered) {
        const productSize: ProductSize = SizesModelsTransformer.createProductSize(apiSize, currencyInfo, productStock);
        productSizes.push(productSize);
      }
    }

    return productSizes;
  }

  /**
   * Parsea los datos de prewarming de la talla provinientes del API añadiendo el
   * campo priceWithCurrency
   * @param prewarming Datos de prewarming asociados a una talla
   * @param currencyInfo Información de la moneda asociada al eCommerceContext en el que nos encontramos
   * @returns Datos completos de prewarming
   */
  public static prewarmingFromApi(prewarming: ApiPrewarming | undefined, currencyInfo: CurrencyInfo): ProductPricePrewarming | undefined {
    if (prewarming) {
      return {
        ...prewarming,
        priceWithCurrency: MoneyHelpers.formatPriceWithCurrencySymbol(parseInt(prewarming.price, 10), currencyInfo)
      };
    }
    return undefined;
  }

}
